import { computed, DestroyRef, inject, Injectable, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CopyTaskDialogResult } from '@app/components/dialogs/copy-task-dialog/copy-task-dialog.component';
import { ExportTasksDialogResult } from '@app/components/dialogs/export-tasks-dialog/export-tasks-dialog.component';
import { SelectGroupsDialogData } from '@app/components/dialogs/select-groups-dialog/select-groups-dialog-data';
import {
  MultipleProjectParticipantsDialogData,
  MultipleProjectParticipantsResultData
} from '@app/components/dialogs/select-multiple-project-participants-dialog/select-multiple-project-participants-dialog.component';
import { SelectParentTaskDialogData } from '@app/components/dialogs/select-parent-task-dialog/select-parent-task-dialog-data';
import { SelectTagsDialogData } from '@app/components/dialogs/select-tags-dialog/select-tags-dialog.component';
import { TagFormDialogResult } from '@app/components/dialogs/tag-form-dialog/tag-form-dialog.component';
import { TaskCreateData } from '@app/components/task/task-create/task-create.component';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { ProjectTaskDTO } from '@app/shared/services/dtos/project-tasks/project-task.dto';
import { CreateTaskDescriptionCommentDTO } from '@app/shared/services/dtos/project-tasks/task-description-comment.dto';
import { ProjectsDataService } from '@app/shared/services/projects-data.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { TagsDataService } from '@app/shared/services/tags-data.service';
import { DialogModeEnum } from '@app/shared/types/enums/dialog-mode.enum';
import { TaskRole } from '@app/shared/types/enums/task-role.enum';
import { Tag, TagFormDialogData } from '@app/shared/types/models/tag/tag.model';
import { TaskDescriptionComment } from '@app/shared/types/models/task/task-description-comment.model';
import { TaskType } from '@app/shared/types/models/task/task-type.model';
import { Task } from '@app/shared/types/models/task/task.model';
import { ActiveTaskService } from '@app/views/company/projects/tasks/active-task.service';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, filter, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { SelectMode } from '../../types/enums/select-mode.enum';
import { TaskPriority } from '../../types/enums/task-priority.enum';
import { TaskStatus } from '../../types/enums/task-status.enum';
import { Group } from '../../types/models/group/group.model';
import { Industry } from '../../types/models/industry/industry.model';
import { Location, SelectSingleLocationDialogData } from '../../types/models/location/location.model';
import { SelectIndustryDialogData } from '../../types/models/project-industry/project-industry.model';
import { ProjectParticipant } from '../../types/models/project/project-participant';
import { TaskContractCost } from '../../types/models/task/task-contract-cost';
import { TaskEstimation } from '../../types/models/task/task-estimation';
import { TaskFilters, TaskListExportParams } from '../../types/models/task/task-filters.model';
import { TaskFinalCost } from '../../types/models/task/task-final-cost';
import { TasksHttpService } from '../http/tasks-http.service';
import { LocationsDataService } from '../locations-data.service';
import { ProjectIndustriesDataService } from '../project-industries-data.service';
import { ProjectParticipantsDataService } from '../project-participants-data.service';
import { DialogService } from '../shared/dialog.service';
import { TaskDescriptionCommentsDataService } from '../task-description-comments-data.service';
import { TaskParticipantsDataService } from '../task-participants-data.service';
import { TasksDataService } from '../tasks-data.service';

/**
 * TasksActionsService - Facade for shared task mutation operations.
 *
 * Centralizes user-triggered actions that modify task data. Uses {@link ActiveProjectService.activeProjectId}
 * for project-scoped loads and creates (no direct {@link ProjectsDataService} dependency for “active project”).
 *
 * Scope: Route-level (`providers` on the project tasks route).
 *
 * Usage: Task sidebar, content, and split view to avoid duplicated modal/toast logic.
 *
 * Architecture:
 * - {@link TasksActionsService}: Mutations, modals, toasts
 * - {@link ActiveProjectService}: Active project id for scoped data
 * - {@link TasksDataService}: Task store and queries
 * - {@link TasksHttpService}: HTTP API calls
 * - {@link TaskDescriptionCommentsDataService}: Task description review comments (create)
 */
@Injectable()
export class TasksActionsService {
  private static readonly EXPORT_PAGE = 0;
  private static readonly EXPORT_LIMIT = 10000;

  private readonly tagsService = inject(TagsDataService);
  private readonly tasksDataService: TasksDataService = inject(TasksDataService);
  private readonly tasksHttpService: TasksHttpService = inject(TasksHttpService);
  private readonly taskDescriptionCommentsDataService: TaskDescriptionCommentsDataService = inject(
    TaskDescriptionCommentsDataService
  );
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(DialogService);
  private readonly projectIndustriesService = inject(ProjectIndustriesDataService);
  private readonly locationsService = inject(LocationsDataService);
  private readonly projectParticipantsService = inject(ProjectParticipantsDataService);
  private readonly taskParticipantsService = inject(TaskParticipantsDataService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly activeProjectService = inject(ActiveProjectService);
  private readonly activeTaskService = inject(ActiveTaskService);
  private readonly activeCompanyService = inject(ActiveCompanyService);
  private readonly projectsDataService = inject(ProjectsDataService);
  private activeProjectId: Signal<string> = this.activeProjectService.activeProjectId;
  private projectIndustries: Signal<Industry[]> = computed(() => {
    const projectIndustries = this.projectIndustriesService.getProjectIndustries(this.activeProjectId())();
    return projectIndustries ? projectIndustries.map(pi => pi.industry()) : [];
  });
  private projectLocations: Signal<Location[]> = computed(() =>
    this.locationsService.getProjectLocations(this.activeProjectId())()
  );

  protected projectGroups: Signal<Group[]> = computed(() =>
    this.projectParticipantsService.getProjectGroups(this.activeProjectId())()
  );
  protected projectParticipants: Signal<ProjectParticipant[]> = computed(() =>
    this.projectParticipantsService.getProjectParticipants(this.activeProjectId())()
  );

  public lockTask(task: Task): void {
    if (!task) return;
    this.tasksDataService
      .lockTask(task.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.toastService.success(this.translateService.instant('project-tasks.toasts.lock-success')));
  }

  public unlockTask(task: Task): void {
    if (!task) return;
    this.tasksDataService
      .unlockTask(task.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.toastService.success(this.translateService.instant('project-tasks.toasts.unlock-success')));
  }

  public deleteTask(task: Task): Observable<ProjectTaskDTO> {
    if (!task) return EMPTY;
    if (task.subtasksCount > 0) {
      this.dialogService.openInformationDialog({
        title: this.translateService.instant('task-confirmation-delete-header'),
        desc: this.translateService.instant('task-confirmation-delete-error-description', {
          taskName: task.name
        })
      });
      return EMPTY;
    }

    return this.dialogService
      .openConfirmationDialog({
        title: this.translateService.instant('task-confirmation-delete-header'),
        desc: this.translateService.instant('task-confirmation-delete-description', {
          taskName: task.name
        })
      })
      .afterClosed()
      .pipe(
        filter(confirmed => confirmed),
        switchMap(() =>
          this.tasksDataService.deleteTask(task).pipe(
            takeUntilDestroyed(this.destroyRef),
            tap(() => this.toastService.success(this.translateService.instant('project-tasks.toasts.delete-success')))
          )
        )
      );
  }

  public copyTaskNumber(task: Task): void {
    const taskNumber = task.taskNumber ?? '';
    navigator.clipboard
      .writeText(taskNumber)
      .then(() => this.toastService.success(this.translateService.instant('project-tasks.toasts.number-copy-success')))
      .catch(() => this.toastService.error(this.translateService.instant('project-tasks.toasts.number-copy-error')));
  }

  public copyTaskLink(task: Task): void {
    if (!task) this.toastService.error(this.translateService.instant('project-tasks.toasts.link-copy-error'));
    const link = task.url();
    navigator.clipboard
      .writeText(link)
      .then(() => this.toastService.success(this.translateService.instant('project-tasks.toasts.link-copy-success')))
      .catch(() => this.toastService.error(this.translateService.instant('project-tasks.toasts.link-copy-error')));
  }

  public copyTask(task: Task): void {
    if (!task) return;

    const companyId = this.activeCompanyService.activeCompanyId();
    const projects = this.projectsDataService.getCompanyProjects(companyId)() ?? [];
    const activeProject = this.activeProjectService.activeProject();

    this.dialogService
      .openCopyTaskDialog({ task, projects, activeProject })
      .afterClosed()
      .pipe(
        filter((result): result is CopyTaskDialogResult => !!result),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(async ({ task: newTask }) => {
        await this.activeTaskService.setActiveTask(newTask);
        this.toastService.success(
          this.translateService.instant('project-tasks.toasts.copy-success', { value: newTask.name })
        );
      });
  }

  public updateTaskType(task: Task, type: TaskType): void {
    this.tasksDataService.updateTaskType(task, type).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  public updateTaskStatus(task: Task, status: TaskStatus): void {
    this.tasksDataService.updateTaskStatus(task, status).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  public updateTaskPriority(task: Task, priority: TaskPriority): void {
    this.tasksDataService.updateTaskPriority(task, priority).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  public updateTaskAcknowledgementRequested(task: Task, acknowledgementRequested: boolean | null): Observable<boolean> {
    if (!task) return of(false);
    return this.tasksDataService.updateTaskAcknowledgementRequested(task, acknowledgementRequested).pipe(
      takeUntilDestroyed(this.destroyRef),
      map(() => true)
    );
  }

  public updateTaskStartDate(task: Task, startDate: Date): void {
    this.tasksDataService.updateStartDate(startDate, task).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  public updateTaskEndDate(task: Task, endDate: Date): void {
    this.tasksDataService.updateEndDate(endDate, task).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  public updateTaskProgress(task: Task, percentage: number): void {
    this.tasksDataService
      .updateCompletionPercentage(percentage, task)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  public openSelectIndustryDialog(task: Task): void {
    const selectedIndustry = task.projectIndustry()?.industry();

    const data: SelectIndustryDialogData = {
      industries: this.projectIndustries,
      selectMode: SelectMode.SINGLE,
      selectedIndustries: selectedIndustry ? [selectedIndustry] : [],
      title: 'project-industries.modals.select-project-industry-modal-title'
    };

    this.dialogService
      .openSelectIndustryDialog(data)
      .afterClosed()
      .pipe(
        switchMap((result: Industry) => {
          if (!result) return EMPTY;

          return this.tasksDataService.updateIndustry(result.id, task).pipe(
            catchError(() => {
              this.toastService.error(this.translateService.instant('project-industries.toasts.update-error'));
              this.openSelectIndustryDialog(task);
              return EMPTY;
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public openSelectLocationDialog(task: Task): void {
    const data: SelectSingleLocationDialogData = {
      locations: this.projectLocations(),
      selectedLocation: task.location(),
      projectId: this.activeProjectId(),
      showEditButton: true,
      isEmptyOptionEnabled: false,
      title: 'project-locations.modals.select-single-location-modal-title'
    };

    this.dialogService
      .openSelectSingleLocationDialog(data)
      .afterClosed()
      .pipe(
        switchMap((result: Location) => {
          if (!result) return EMPTY;

          return this.tasksDataService.updateLocation(result, task).pipe(
            catchError(() => {
              this.openSelectLocationDialog(task);
              return EMPTY;
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public updateTaskEstimation(task: Task, estimation: TaskEstimation): void {
    this.tasksDataService.updateEstimation(estimation, task).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  public updateTaskFinalCost(task: Task, finalCost: TaskFinalCost): void {
    this.tasksDataService.updateFinalCost(finalCost, task).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  public openEditFinalCostDialog(task: Task): void {
    this.dialogService
      .openFinalCostDialog({ finalCost: task.finalCost, autoFocus: false })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: TaskFinalCost | undefined) => {
        if (result) {
          this.updateTaskFinalCost(task, result);
        }
      });
  }

  public updateTaskContractCost(task: Task, contractCost: TaskContractCost): void {
    this.tasksDataService.updateContractCost(contractCost, task).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  public openEditContractCostDialog(task: Task): void {
    this.dialogService
      .openContractCostDialog({ contractCost: task.contractCost, autoFocus: false })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: TaskContractCost | undefined) => {
        if (result) {
          this.updateTaskContractCost(task, result);
        }
      });
  }

  public openSelectGroupDialog(task: Task): void {
    const selectedGroup = task.projectGroupAdministrator()?.group();
    const data: SelectGroupsDialogData = {
      groups: this.projectGroups,
      selectedGroups: selectedGroup ? [selectedGroup] : [],
      lockedGroups: [],
      selectionMode: SelectMode.SINGLE,
      allowHierarchy: false
    };

    this.dialogService
      .openSelectGroupsDialog(data)
      .afterClosed()
      .pipe(
        switchMap(selectedGroups => {
          const selectedGroupId = selectedGroups?.[0]?.id;
          if (!selectedGroupId) return EMPTY;

          return this.tasksDataService.updateTaskGroup(selectedGroupId, task).pipe(
            catchError(() => {
              this.openSelectGroupDialog(task);
              return EMPTY;
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  // group participants selection
  public openSelectParticipantsDialog(task: Task): void {
    const groupParticipants = this.projectParticipants()?.filter(
      pp => pp.group()?.id === task.projectGroupAdministrator().group().id
    );

    const groupAdminId = task.projectGroupAdministrator().id;

    const taskParticipants = this.taskParticipantsService.getTaskParticipants(task.id)() ?? [];

    const industryAdminId = taskParticipants.find(pp => pp.role === TaskRole.INDUSTRYADMIN)?.projectParticipant()?.id;

    const groupParticipantIds = new Set((groupParticipants ?? []).map(pp => pp.id));
    const preselectedProjectParticipants = taskParticipants
      .map(tp => tp.projectParticipant())
      .filter(pp => pp != null && groupParticipantIds.has(pp.id));

    const data: MultipleProjectParticipantsDialogData = {
      projectParticipants: groupParticipants,
      preselectedProjectParticipants,
      preselectedLockedProjectParticipantsIds: [industryAdminId, groupAdminId].filter(Boolean),
      title: 'project-participants.modals.select-multiple-project-participants'
    };

    this.dialogService
      .openSelectMultipleProjectParticipantsDialog(data)
      .afterClosed()
      .pipe(
        switchMap((result: MultipleProjectParticipantsResultData) => {
          if (!result) return EMPTY;

          const requests: Observable<unknown>[] = [];

          if (result.idsToAdd.length > 0) {
            requests.push(this.taskParticipantsService.addTaskUsers(task.id, result.idsToAdd));
          }
          if (result.idsToRemove.length > 0) {
            requests.push(this.taskParticipantsService.removeTaskUsers(task.id, result.idsToRemove));
          }

          return requests.length > 0
            ? forkJoin(requests).pipe(
                catchError(() => {
                  this.openSelectParticipantsDialog(task);
                  return EMPTY;
                })
              )
            : EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public openMoveTaskDialog(task: Task): void {
    if (!task) return;
    const data: SelectParentTaskDialogData = {
      taskToMove: task,
      projectId: this.activeProjectId()
    };

    this.dialogService
      .openSelectParentTaskDialog(data)
      .afterClosed()
      .pipe(
        filter((moved): moved is true => moved === true),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.toastService.success(this.translateService.instant('project-tasks.toasts.move-success'));
      });
  }

  public createNewTask(createData: TaskCreateData): Observable<ProjectTaskDTO[]> {
    return this.tasksDataService.createNewTask(this.activeProjectId(), createData).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(res => {
        const newTask = res[0];
        this.toastService.success(
          this.translateService.instant('project-tasks.toasts.successfully-created', {
            value: newTask.name
          })
        );
      })
    );
  }

  public updateTaskName(task: Task, name: string): Observable<ProjectTaskDTO> {
    return this.tasksDataService.updateTaskName(task, name).pipe(takeUntilDestroyed(this.destroyRef));
  }

  public updateTaskDescription(
    task: Task,
    description: string,
    requireConfirmationAgain?: boolean
  ): Observable<ProjectTaskDTO> {
    return this.tasksDataService
      .updateTaskDescription(task, description, requireConfirmationAgain)
      .pipe(takeUntilDestroyed(this.destroyRef));
  }

  public submitTaskDescriptionComment(
    task: Task,
    payload: CreateTaskDescriptionCommentDTO
  ): Observable<TaskDescriptionComment> {
    const taskId = task?.id;
    if (!taskId) {
      return EMPTY;
    }

    return this.taskDescriptionCommentsDataService.createDescriptionComment(taskId, payload).pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(() => {
        this.toastService.error(this.translateService.instant('task-description-comments.toast-error'));
        return EMPTY;
      })
    );
  }

  public exportTasks(exportParams: TaskListExportParams): void {
    const projectId = this.activeProjectId();
    if (!projectId) return;

    this.dialogService
      .openExportTasksDialog()
      .afterClosed()
      .pipe(
        filter((result): result is ExportTasksDialogResult => result != null),
        switchMap(({ includeSubtasks }) => {
          const normalizedFilters: TaskFilters | undefined = exportParams.filters
            ? { ...exportParams.filters }
            : undefined;
          if (normalizedFilters) {
            delete (normalizedFilters as Record<string, unknown>)['direction'];
            delete (normalizedFilters as Record<string, unknown>)['limit'];
            delete (normalizedFilters as Record<string, unknown>)['page'];
          }

          const request = this.tasksHttpService.buildRootTasksRequest(
            TasksActionsService.EXPORT_PAGE,
            TasksActionsService.EXPORT_LIMIT,
            exportParams.orderBy,
            exportParams.order,
            exportParams.searchPhrase ?? normalizedFilters?.text,
            exportParams.taskFiltersId ?? undefined,
            normalizedFilters
          );

          return this.tasksHttpService.exportTasks(projectId, request, includeSubtasks);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: response => {
          const blob = response.body;
          if (!blob) {
            this.toastService.error(this.translateService.instant('project-tasks.toasts.export-error'));
            return;
          }

          let fileName = 'tasks-export.xlsx';
          const contentDisposition = response.headers.get('Content-Disposition');
          if (contentDisposition) {
            const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = fileNameRegex.exec(contentDisposition);
            if (matches?.[1]) {
              fileName = matches[1].replace(/['"]/g, '');
            }
          }

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          this.toastService.success(this.translateService.instant('project-tasks.toasts.export-success'));
        },
        error: () => {
          this.toastService.error(this.translateService.instant('project-tasks.toasts.export-error'));
        }
      });
  }

  /** Route-owned tag workflow: survives renderer remounts without a second store. */
  public openTaskTagsEditor(task: Task): void {
    // Capture identity when opening: a result must never target a later active task/project.
    const taskId = task.id;
    const projectId = this.activeProjectId();
    const projectTags = computed(() => this.tagsService.getProjectTags(projectId)());
    const data: SelectTagsDialogData = {
      tags: projectTags,
      chosenTags: this.tagsService.getTaskTags(taskId)() || [],
      onAddTag: () => this.openAddTaskTagDialog(projectId),
      onEditTag: (tag: Tag) => this.openEditTaskTagDialog(projectId, tag)
    };

    this.dialogService
      .openSelectTagsDialog(data)
      .afterClosed()
      .pipe(
        filter(selectedTags => !!selectedTags),
        switchMap((selectedTags: Tag[]) => {
          const tagIds = selectedTags.map(t => t.id);
          return this.tagsService.updateTaskTags(taskId, tagIds);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private openAddTaskTagDialog(projectId: string): void {
    this.dialogService
      .openTagFormDialog({})
      .afterClosed()
      .pipe(
        filter((result: TagFormDialogResult) => !!result),
        switchMap((result: TagFormDialogResult) => {
          return this.tagsService.createTag(
            projectId,
            new Tag({
              ...result,
              projectId
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private openEditTaskTagDialog(projectId: string, tag: Tag): void {
    const data: TagFormDialogData = { projectTagToEdit: tag, mode: DialogModeEnum.EDIT };
    this.dialogService
      .openTagFormDialog(data)
      .afterClosed()
      .pipe(
        filter(result => !!result),
        switchMap(result => this.tagsService.updateTag(projectId, tag.id, result)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
